import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import NavigationBar from './NavigationBar';
import LoadingSpinner from './LoadingSpinner';
import './TermsOfService.css';

const TermsOfService = () => {
    const navigate = useNavigate();
    const { updateTermsAcceptance } = useAuth();
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [accepting, setAccepting] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading Terms of Service...');

    useEffect(() => {
        waitForIcJwtAndFetchContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Helper function to delay
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Wait for ic_jwt to be ready, then fetch content
    const waitForIcJwtAndFetchContent = async (maxWaitAttempts = 15, pollInterval = 1000) => {
        setLoading(true);
        setError('');

        // First, poll for ic_jwt readiness
        for (let attempt = 1; attempt <= maxWaitAttempts; attempt++) {
            try {
                setLoadingMessage(
                    attempt === 1 
                        ? 'Preparing authentication...' 
                        : `Waiting for authentication to complete... (${attempt}/${maxWaitAttempts})`
                );

                const readyResponse = await apiService.checkIcJwtReady();

                if (readyResponse.data.ready) {
                    console.log('ic_jwt is ready, fetching TOS content');
                    break; // ic_jwt is ready, proceed to fetch content
                }
                
                if (attempt === maxWaitAttempts) {
                    console.warn('ic_jwt not ready after max wait attempts');
                    setError('Authentication is taking longer than expected. Please try refreshing the page or logging in again.');
                    setLoading(false);
                    return;
                }
                
                console.log(`ic_jwt not ready, waiting ${pollInterval}ms (attempt ${attempt}/${maxWaitAttempts})`);
                await delay(pollInterval);
                
            } catch (err) {
                console.error(`Error checking ic_jwt readiness (attempt ${attempt}):`, err);

                // If it's a network error or server error, keep trying
                if (attempt < maxWaitAttempts) {
                    await delay(pollInterval);
                    continue;
                }

                setError('Failed to verify authentication. Please try again.');
                setLoading(false);
                return;
            }
        }

        // Now fetch the actual TOS content
        setLoadingMessage('Loading Terms of Service...');
        await fetchTermsContent();
    };

    // Fetch the TOS content (called after ic_jwt is confirmed ready)
    const fetchTermsContent = async () => {
        try {
            const response = await apiService.getTermsOfServiceContent();

            if (response.data.success) {
                setContent(response.data.content);
            } else {
                setError(response.data.error || 'Failed to load terms of service');
            }
        } catch (err) {
            console.error('Error fetching terms of service:', err);
            setError('Failed to load terms of service. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    // Retry handler - goes through full flow again
    const handleRetry = () => {
        waitForIcJwtAndFetchContent();
    };

    const handleAccept = async () => {
        try {
            setAccepting(true);
            const response = await apiService.acceptTermsOfService();

            if (response.data.success) {
                // Update the auth context
                updateTermsAcceptance(true);

                // Redirect to the last event or home
                const lastEvent = localStorage.getItem('last_event');
                if (lastEvent) {
                    navigate(`/event/${lastEvent}/home`);
                } else {
                    navigate('/login');
                }
            } else {
                setError('Failed to accept terms of service. Please try again.');
            }
        } catch (err) {
            console.error('Error accepting terms of service:', err);
            setError('Failed to accept terms of service. Please try again.');
        } finally {
            setAccepting(false);
        }
    };

    if (loading) {
        return (
            <div className="tos-page">
                <NavigationBar />
                <div className="tos-container">
                    <LoadingSpinner message={loadingMessage} />
                </div>
            </div>
        );
    }

    return (
        <div className="tos-page">
            <NavigationBar />
            <div className="tos-container">
                <div className="tos-header">
                    <h1>Terms of Service</h1>
                </div>

                {error && (
                    <div className="tos-error">
                        <p>{error}</p>
                        <button onClick={handleRetry} className="tos-retry-btn">
                            Try Again
                        </button>
                    </div>
                )}

                {!error && content && (
                    <>
                        <div
                            className="tos-content"
                            dangerouslySetInnerHTML={{ __html: content }}
                        />

                        <div className="tos-footer">
                            <p className="tos-footer-text">
                                Before proceeding, please review and accept the Terms of Service by clicking 'Accept Terms of Service'.
                            </p>
                            <button
                                className="tos-accept-btn"
                                onClick={handleAccept}
                                disabled={accepting}
                            >
                                {accepting ? 'Accepting...' : 'Accept Terms of Service'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TermsOfService;
